import { Alert, Box, Button, Col, Spinner, Text } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import { fetchJson } from '../../store/fetch';
import { translateKey } from '../../translate';
import { DangerAlertWhenErrorCode } from './common';
import { DisplayMessage } from './DisplayMessage';

export enum DownloadProgressFeedbackType {
    percentageText = 'percentageText',
    progressPercentage = 'progressPercentage',
    spinner = 'spinner',
}

interface IExportDownloadProgressProps {
    downloadProcessFeedbackType: DownloadProgressFeedbackType;
    exportDownloadId?: string;
    onFinished: (error?: boolean) => Promise<void> | void;
    stacked?: boolean;
}

export function ExportDownloadProgress(props: IExportDownloadProgressProps) {
    const [pollingTimeout, setPollingTimeout] = useState<number>();
    const [isPolling, setIsPolling] = useState<boolean>(false);
    const [progressPercentage, setProgressPercentage] = useState<number>();
    const [exportDownloadError, setExportDownloadError] = useState<string>();

    useEffect(() => {
        if (props.exportDownloadId) {
            setIsPolling(true);
        }
    }, [props.exportDownloadId]);

    useEffect(() => {
        if (isPolling) {
            setExportDownloadError(undefined);
            poll();
        }
    }, [isPolling]);

    async function poll() {
        window.clearTimeout(pollingTimeout);
        try {
            const res = await fetchJson(`/api/exportDownloads/${props.exportDownloadId}`, 'GET');
            if (res.json.error) {
                setIsPolling(false);
                setExportDownloadError(res.json.error);
                await props.onFinished(true);
            } else if (res.json.ready) {
                setIsPolling(false);
                await props.onFinished();
                window.open(`/api/exportDownloads/${props.exportDownloadId}/${res.json.filename}`, '_blank');
            } else {
                setProgressPercentage(res.json.progressPercentage);
                setPollingTimeout(
                    window.setTimeout(async () => {
                        await poll();
                    }, 1000)
                );
            }
        } catch (e) {
            console.log(e);
            window.clearTimeout(pollingTimeout);
            setExportDownloadError('ERROR');
            setIsPolling(false);
            await props.onFinished();
        }
    }

    function renderElements() {
        if (props.stacked) {
            return (
                <Alert appearance="info">
                    <Col>
                        <Alert.Text
                            as="p"
                            fontWeight={'bold'}
                            mb={1}
                            size="sm"
                        >
                            {translateKey('export.downloading')}
                        </Alert.Text>
                        <Alert.Text
                            as="p"
                            fontWeight={'bold'}
                            mb={2}
                            size="sm"
                        >
                            {translateKey('export.downloadProgress', `${progressPercentage || 0}`)}
                        </Alert.Text>
                        <Alert.Button
                            onClick={() => abortDownload()}
                            sx={{ mt: 2 }}
                        >
                            {translateKey('export.cancelDownload')}
                        </Alert.Button>
                    </Col>
                </Alert>
            );
        } else {
            return (
                <Box mt={4}>
                    <Button
                        onClick={() => abortDownload()}
                        variant="outline"
                    >
                        {translateKey('common.cancel')}
                    </Button>
                    <DisplayMessage
                        message={translateKey('export.exportingOneMomentPlease', `${progressPercentage || 0}`)}
                        messageType="info"
                        onClose={async () => abortDownload()}
                    />
                </Box>
            );
        }
    }

    function abortDownload() {
        window.clearTimeout(pollingTimeout);
        setIsPolling(false);
        props.onFinished();
    }

    switch (props.downloadProcessFeedbackType) {
        case DownloadProgressFeedbackType.progressPercentage:
        case DownloadProgressFeedbackType.spinner:
            return (
                <>
                    {isPolling && renderElements()}
                    {props.downloadProcessFeedbackType === DownloadProgressFeedbackType.spinner && <Spinner />}
                    <DangerAlertWhenErrorCode errorCode={exportDownloadError} />
                </>
            );
        case DownloadProgressFeedbackType.percentageText:
        default:
            return <Text size="sm">{progressPercentage}%</Text>;
    }
}
